import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { STATUS } from 'constants/miscConstants';
import AdminService from 'services/AdminService';
import { t } from 'i18next';

const { SUCCESS, ERROR } = STATUS;

export const initialState = {
  loading: false,
  message: '',
  showMessage: false,
  status: SUCCESS,
  usersList: [],
  statsLoading: false,
  userStats: {},
  averageSessionTimeLoading: false,
  averageSessionTime: {},
  sendInvitationSuccess: false,
  totalCountLoading: false,
  totalUsersCount: 0,
  reactivateLoading: false,
  reactivatingUserId: null,
  reactivateSuccess: false,
  filters: {
    pageNumber: 1,
    pageSize: 10,
  },
};

export const getUsersActivity = createAsyncThunk('userActivity/getUsersActivity', async (data, { rejectWithValue }) => {
  const { queryParams, pageNumber, pageSize } = data;
  try {
    const response = await AdminService.getUsersActivity(queryParams);
    return { response, pageNumber, pageSize };
  } catch (err) {
    return rejectWithValue(err?.message || 'Something went wrong');
  }
});

export const getUsersStats = createAsyncThunk('userActivity/getUsersStats', async (data, { rejectWithValue }) => {
  const { queryParams } = data;
  try {
    const response = await AdminService.getNewUserStats(queryParams);
    return response;
  } catch (err) {
    return rejectWithValue(err?.message || 'Something went wrong');
  }
});

export const getAverageTimeSession = createAsyncThunk('userActivity/getAverageTimeSession', async (data, { rejectWithValue }) => {
  try {
    const response = await AdminService.getAverageTimeSession();
    return response;
  } catch (err) {
    return rejectWithValue(err?.message || 'Something went wrong');
  }
});

export const sendInvitation = createAsyncThunk('userActivity/sendInvitation', async (data, { rejectWithValue }) => {
  try {
    const response = await AdminService.sendInvitation(data);
    return response;
  } catch (err) {
    return rejectWithValue(err?.message || 'Something went wrong');
  }
});

export const getTotalUserCount = createAsyncThunk('userActivity/getTotalUserCount', async (data, { rejectWithValue }) => {
  try {
    const response = await AdminService.getTotalCount(data);
    return response;
  } catch (err) {
    return rejectWithValue(err?.message || 'Something went wrong');
  }
});

export const reactivateAccount = createAsyncThunk('userActivity/reactivateAccount', async (data, { rejectWithValue }) => {
  const { id } = data;
  try {
    const response = await AdminService.reactivateAccount(id);
    return response;
  } catch (err) {
    return rejectWithValue(err?.message || 'Failed to reactivate account');
  }
});

export const userActivitySlice = createSlice({
  name: 'userActivity',
  initialState,
  reducers: {
    hideMessage: (state) => {
      state.message = '';
      state.status = SUCCESS;
      state.showMessage = false;
      state.sendInvitationSuccess = false;
      state.reactivateSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsersActivity.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUsersActivity.fulfilled, (state, action) => {
        const { response, pageNumber, pageSize } = action.payload;
        state.filters.pageNumber = pageNumber;
        state.filters.pageSize = pageSize;
        state.usersList = response;
        state.loading = false;
      })
      .addCase(getUsersActivity.rejected, (state, action) => {
        state.loading = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(getUsersStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(getUsersStats.fulfilled, (state, action) => {
        state.userStats = action.payload;
        state.statsLoading = false;
      })
      .addCase(getUsersStats.rejected, (state, action) => {
        state.statsLoading = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(getAverageTimeSession.pending, (state) => {
        state.averageSessionTimeLoading = true;
      })
      .addCase(getAverageTimeSession.fulfilled, (state, action) => {
        state.averageSessionTime = action.payload;
        state.averageSessionTimeLoading = false;
      })
      .addCase(getAverageTimeSession.rejected, (state, action) => {
        state.averageSessionTimeLoading = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(sendInvitation.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendInvitation.fulfilled, (state) => {
        state.message = t('component.user.activity.actions.sendCompletionnEmail.success');
        state.status = SUCCESS;
        state.sendInvitationSuccess = true;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(sendInvitation.rejected, (state, action) => {
        state.loading = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      })

      .addCase(getTotalUserCount.pending, (state) => {
        state.totalCountLoading = true;
      })
      .addCase(getTotalUserCount.fulfilled, (state, action) => {
        state.totalUsersCount = action.payload;
        state.totalCountLoading = false;
      })
      .addCase(getTotalUserCount.rejected, (state, action) => {
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
        state.totalCountLoading = false;
      })

      .addCase(reactivateAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(reactivateAccount.fulfilled, (state) => {
        state.loading = false;
        state.reactivateSuccess = true;
        state.status = SUCCESS;
        state.message = t('component.user.activity.reactivate.success');
        state.showMessage = true;
      })
      .addCase(reactivateAccount.rejected, (state, action) => {
        state.loading = false;
        state.reactivateSuccess = false;
        state.status = ERROR;
        state.message = action.payload;
        state.showMessage = true;
      });
  },
});

export const { hideMessage } = userActivitySlice.actions;

export default userActivitySlice.reducer;
